/**
 * One VHAL subscription per emulator session (doc 06 §Subscription manager).
 *
 * The VHAL service is poll-based today, so this provider polls the batched
 * `getAllPropertyValues` once a second while any consumer is mounted, and
 * layers on:
 *  - optimistic writes: the user's target renders immediately;
 *  - the drag guard: while a slider is dragged, incoming values for that
 *    property are ignored until 300 ms after release;
 *  - write acks (`PROP · AREA · ack N ms`) and timeline event markers;
 *  - a 40 s history for watched properties (timeline lanes).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useCuttlefishVhal } from "../cuttlefish/hooks/useCuttlefishVhal";
import { VhalPropertyChangeMode, VhalPropertyId, type VhalPropertyValue } from "../cuttlefish/types/vhal";
import {
  buildCatalog,
  propName,
  valueKey,
  type PropertyCatalog,
  type PropertyValues,
  type Write,
  type WriteResult,
} from "./registry";

export const TIMELINE_WINDOW_MS = 40_000;
const POLL_MS = 1000;
/** Longest gap between polls while the VHAL keeps failing (e.g. the emulator is restarting). */
const POLL_MAX_MS = 15_000;
const GUARD_RELEASE_MS = 300;

export type Ack = { prop: string; area: string; ms: number; ok: boolean; error?: string };
export type TimelineEvent = { t: number; label: string };
export type Sample = { t: number; v: number };

type Ctx = {
  available: boolean;
  discovering: boolean;
  transport: "grpc" | "adb";
  catalog: PropertyCatalog;
  catalogSource: "device" | "static" | null;
  values: PropertyValues;
  write: (batch: Write[], label?: string) => Promise<WriteResult>;
  guard: (prop: number, areaId: number, on: boolean) => void;
  lastAck: Ack | null;
  events: TimelineEvent[];
  addEvent: (label: string) => void;
  watched: number[];
  toggleWatch: (prop: number) => void;
  history: Record<number, Sample[]>;
  retry: () => void;
  error?: string;
};

const VhalSubscriptionContext = createContext<Ctx | null>(null);

export function useVhalSubscription(): Ctx {
  const c = useContext(VhalSubscriptionContext);
  if (!c) throw new Error("useVhalSubscription must be used within VhalSubscriptionProvider");
  return c;
}

const sampleValue = (v?: VhalPropertyValue) => v?.floatValues?.[0] ?? v?.int32Values?.[0];

export function VhalSubscriptionProvider({ children }: { children: ReactNode }) {
  const vhal = useCuttlefishVhal();
  const [values, setValues] = useState<PropertyValues>(new Map());
  const [lastAck, setLastAck] = useState<Ack | null>(null);
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [watched, setWatched] = useState<number[]>([VhalPropertyId.PERF_VEHICLE_SPEED]);
  const [history, setHistory] = useState<Record<number, Sample[]>>({});
  const overlay = useRef(new Map<string, { value: VhalPropertyValue; until: number }>());
  const guards = useRef(new Map<string, number>()); // key → release time (Infinity while dragging)

  const catalog = useMemo(
    () =>
      buildCatalog(
        vhal.discoveredProperties ?? [],
        vhal.catalogSource === "device" ? "device" : "static",
        (vhal.transport ?? "grpc") === "grpc",
      ),
    [vhal.discoveredProperties, vhal.catalogSource, vhal.transport],
  );

  const merge = useCallback((incoming: Map<string, VhalPropertyValue>) => {
    const now = Date.now();
    setValues((prev) => {
      const next = new Map(prev);
      incoming.forEach((v, k) => {
        const g = guards.current.get(k);
        if (g != null && now < g) return; // drag guard
        const o = overlay.current.get(k);
        if (o && now < o.until && sampleValue(o.value) !== sampleValue(v)) return; // awaiting echo
        overlay.current.delete(k);
        next.set(k, v);
      });
      return next;
    });
  }, []);

  // Poll while available. The loop reads the service through a ref: every
  // failed read records an error, which rebuilds the service object, and
  // restarting the loop on that would re-poll immediately in a tight loop.
  // Failed or empty reads back off to POLL_MAX_MS until values return.
  const vhalRef = useRef(vhal);
  useEffect(() => {
    vhalRef.current = vhal;
  }, [vhal]);
  useEffect(() => {
    if (!vhal.isAvailable) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    let delay = POLL_MS;
    const tick = async () => {
      let got = 0;
      try {
        const all = await vhalRef.current.getAllPropertyValues();
        if (stop) return;
        got = all.size;
        if (got) merge(all as Map<string, VhalPropertyValue>);
      } catch {
        /* transient; retried below */
      }
      if (stop) return;
      delay = got ? POLL_MS : Math.min(delay * 2, POLL_MAX_MS);
      timer = setTimeout(tick, delay);
    };
    void tick();
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [vhal.isAvailable, merge]);

  // Timeline history for watched properties.
  useEffect(() => {
    const t = Date.now();
    setHistory((h) => {
      const out: Record<number, Sample[]> = {};
      for (const p of watched) {
        const v = sampleValue(values.get(valueKey(p, 0)));
        const prev = (h[p] ?? []).filter((s) => t - s.t < TIMELINE_WINDOW_MS);
        out[p] = v == null ? prev : [...prev, { t, v }];
      }
      return out;
    });
  }, [values, watched]);

  const addEvent = useCallback((label: string) => {
    const t = Date.now();
    setEvents((e) => [...e.filter((x) => t - x.t < TIMELINE_WINDOW_MS), { t, label }]);
  }, []);

  const write = useCallback(
    async (batch: Write[], label?: string): Promise<WriteResult> => {
      const start = performance.now();
      // optimistic overlay
      setValues((prev) => {
        const next = new Map(prev);
        for (const w of batch) {
          // Continuous props (speed) show what the device reports, not the target.
          if (catalog.get(w.prop)?.changeMode === VhalPropertyChangeMode.CONTINUOUS) continue;
          const k = valueKey(w.prop, w.areaId ?? 0);
          overlay.current.set(k, { value: w.value, until: Date.now() + 3000 });
          next.set(k, w.value);
        }
        return next;
      });
      const results = await Promise.all(
        batch.map((w) => vhal.setProperty(w.value).catch((e: unknown) => (e instanceof Error ? e.message : false))),
      );
      const ms = Math.round(performance.now() - start);
      const errors = results.filter((r) => r !== true).map((r) => (typeof r === "string" ? r : "Write rejected"));
      const first = batch[0];
      if (errors.length) {
        // revert to last streamed value
        batch.forEach((w) => overlay.current.delete(valueKey(w.prop, w.areaId ?? 0)));
      }
      if (first) {
        setLastAck({
          prop: propName(first.prop),
          area: first.areaId ? `0x${first.areaId.toString(16)}` : "GLOBAL",
          ms,
          ok: errors.length === 0,
          error: errors[0],
        });
      }
      if (label && !errors.length) addEvent(label);
      return { ok: errors.length === 0, ms, errors };
    },
    [vhal, addEvent, catalog],
  );

  const guard = useCallback((prop: number, areaId: number, on: boolean) => {
    guards.current.set(valueKey(prop, areaId), on ? Infinity : Date.now() + GUARD_RELEASE_MS);
  }, []);

  const toggleWatch = useCallback(
    (prop: number) => setWatched((w) => (w.includes(prop) ? w.filter((x) => x !== prop) : [...w, prop])),
    [],
  );

  const value = useMemo<Ctx>(
    () => ({
      available: vhal.isAvailable,
      discovering: vhal.isDiscovering,
      transport: vhal.transport ?? "grpc",
      catalog,
      catalogSource: vhal.catalogSource ?? null,
      values,
      write,
      guard,
      lastAck,
      events,
      addEvent,
      watched,
      toggleWatch,
      history,
      retry: () => vhal.retry?.(),
      error: vhal.lastError?.message,
    }),
    [vhal, catalog, values, write, guard, lastAck, events, addEvent, watched, toggleWatch, history],
  );

  return <VhalSubscriptionContext.Provider value={value}>{children}</VhalSubscriptionContext.Provider>;
}
